import type { FetcherError } from '@bino/utils/fetcher';
import { createSharedState, useSharedState } from '@bino/utils/hooks';
import { getCookie } from '@bino/utils/functions/cookie';
import type { ReactNode } from 'react';
import { createContext, use, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { KeyedMutator } from 'swr';
import useSWR from 'swr';
import type { BiaxCartResponse } from '../types';

// Module level rather than component state: the page mounts several React roots, each with its own
// provider, so this is what lets Apply now in one of them open the cart in the navbar
const cartOpenStore = createSharedState(false);

type CartContextType = {
    productSearchUrl?: string;
    isAuthorized: boolean;
    userName?: string;
    cart: BiaxCartResponse;
    loadingCart: boolean;
    refreshCart: KeyedMutator<BiaxCartResponse>;
    error: unknown;
    globalCartError: FetcherError | null;
    setGlobalCartError: (error: FetcherError | null) => void;
    disableCart: boolean;
    emergencyContactFormUrl?: string;
    cartOpen: boolean;
    setCartOpen: (open: boolean) => void;
};

const CartContext = createContext<CartContextType | undefined>(undefined);

export type ShoppingCartProviderProps = {
    productSearchUrl?: string;
    isAuthorized: boolean;
    userName?: string;
    children: ReactNode;
    disableCart: boolean;
    emergencyContactFormUrl?: string;
};

const cartUrl = (isDisabledCart: boolean, isAuthorized: boolean, language: string) => {
    const cartIdCookie = getCookie('CartId');
    const path = `/api/cart?lang=${language}`;
    return isAuthorized ? path : isDisabledCart || !cartIdCookie ? null : path;
};

const isAuthError = (error: FetcherError | null) => error?.status === 401 || error?.status === 403;
const isCartMergedError = (error: FetcherError | null) => error?.status === 410;
const emptyCart: BiaxCartResponse = { items: [], biaxCartDetailPageUrl: '' };
export const ShoppingCartProvider = ({
    productSearchUrl,
    isAuthorized,
    userName,
    children,
    disableCart,
    emergencyContactFormUrl,
}: ShoppingCartProviderProps) => {
    const cartOpen = useSharedState(cartOpenStore);
    const [globalCartError, setGlobalCartError] = useState<FetcherError | null>(null);
    const { i18n } = useTranslation();

    const {
        data: cart = emptyCart,
        isLoading,
        isValidating,
        mutate,
        error,
    } = useSWR<BiaxCartResponse, FetcherError>(() => cartUrl(disableCart, isAuthorized, i18n.language), {
        shouldRetryOnError: false,
        keepPreviousData: true,
        focusThrottleInterval: 60_000,
        onError: (error) => {
            if (isAuthError(error)) {
                setGlobalCartError(error);
            } else if (isCartMergedError(error)) {
                const redirectAfterLogin = encodeURIComponent(window.location.href);
                window.location.href = `/api/cart/ensure-logged-in?redirectUrl=${redirectAfterLogin}`;
            }
        },
    });

    return (
        <CartContext
            value={{
                productSearchUrl,
                isAuthorized,
                userName,
                cart,
                refreshCart: mutate,
                loadingCart: isLoading || isValidating,
                error,
                globalCartError,
                setGlobalCartError,
                disableCart,
                emergencyContactFormUrl,
                cartOpen,
                setCartOpen: cartOpenStore.set,
            }}
        >
            {children}
        </CartContext>
    );
};

export const useCartContext = () => {
    const context = use(CartContext);
    if (context === undefined) {
        throw new Error('useCartContext must be used within a ShoppingCartProvider');
    }
    return context;
};
